import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Calendar, Modal, Tag, Popover } from 'antd';
import dayjs from 'dayjs';
import _deepClone from 'lodash/cloneDeep';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import isBetweenPlugin from 'dayjs/plugin/isBetween';

import styles from './calendar.module.scss';
import { KoAffix, KoButton, KoCharts, KoDescription, KoDropdown, KoEngagementCalendar,  KoStaicMembers, KoText, KoTitle, KoTooltips } from 'packages';
import KoIcon from '@kwapio/icons';
import { assignColumnsToGroup, getCurrentMonthDisplayInfo, getOverlappingGroups } from 'shared/utils';
import BookingTrendChart from 'pages/engagements/events/bookingTrendChart';
import { getCookie } from 'shared/utils/cookies';
import { Link } from 'react-router-dom';

dayjs.extend(utc);
dayjs.extend(localizedFormat);
dayjs.extend(timezone);
dayjs.extend(isBetweenPlugin);

// Common
const slotHeight = 65;

const getEllipsisRows = (h) => {
  if (h < 100) return 2;
  if (h < 200) return 3;
  if (h < 300) return 4;
  return 5;
};

const userColorMap = {
  ticket: 'blue',
  meeting: 'volcano',
  events: 'geekblue'
};

const getPriority = (event) => {
  const name = event.name?.toLowerCase() || '';
  if (name.includes('error')) return 3;
  if (name.includes('warning')) return 2;
  if (name.includes('check-in') || name.includes('sync')) return 1;
  return 0;
};

const expandRecurringMeetings = (meetings, daysForward = 30) => {
  const expanded = [];

  meetings.forEach(meeting => {
    const { startDate, endDate, startTime, endTime, reoccurrence } = meeting;
    const baseDate = dayjs(startDate).startOf('day');
    const finalDate = dayjs(endDate).startOf('day');

    if (!startTime || !endTime) {
      console.warn('Missing startTime or endTime in meeting:', meeting);
      return;
    }

    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);

    if (
      isNaN(startHour) || isNaN(startMinute) ||
      isNaN(endHour) || isNaN(endMinute)
    ) {
      console.warn('Invalid time format in meeting:', meeting);
      return;
    }

    const duration = (endHour * 60 + endMinute) - (startHour * 60 + startMinute);

    const createInstance = (suffix, instanceDate) => {
      const instStart = instanceDate.clone().hour(startHour).minute(startMinute);
      const instEnd = instStart.clone().add(duration, 'minute');

      expanded.push({
        ...meeting,
        meetingId: `${meeting.meetingId}`,
        startTime: instStart.format(),
        endTime: instEnd.format()
      });
    };

    const recurrence = reoccurrence?.toLowerCase();

    // Handle defined recurrence types
    if (recurrence === 'daily' || recurrence === '1') {
      for (let i = 0; i <= daysForward; i++) {
        createInstance(`d${i}`, baseDate.clone().add(i, 'day'));
      }
    } else if (recurrence === 'weekly' || recurrence === '2') {
      for (let i = 0; i <= Math.floor(daysForward / 7); i++) {
        createInstance(`w${i}`, baseDate.clone().add(i, 'week'));
      }
    } else if (recurrence === 'weekends' || recurrence === '3') {
      for (let i = 0; i <= daysForward; i++) {
        const date = baseDate.clone().add(i, 'day');
        if ([0, 6].includes(date.day())) {
          createInstance(`we${i}`, date);
        }
      }
    } else if (recurrence === 'monthly' || recurrence === '4') {
      for (let i = 0; i <= Math.floor(daysForward / 30); i++) {
        createInstance(`m${i}`, baseDate.clone().add(i, 'month'));
      }
    } else if (recurrence === 'weekday' || recurrence === '5') {
      for (let i = 0; i <= daysForward; i++) {
        const date = baseDate.clone().add(i, 'day');
        if (date.day() >= 1 && date.day() <= 5) {
          createInstance(`wd${i}`, date);
        }
      }
    } else {
      // 🔥 Default fallback: If no recurrence but endDate exists
      const diffDays = finalDate.diff(baseDate, 'day');
      if (diffDays > 0) {
        for (let i = 0; i <= diffDays; i++) {
          createInstance(`nd${i}`, baseDate.clone().add(i, 'day'));
        }
      } else {
        // Only one instance
        createInstance(`nd0`, baseDate);
      }
    }
  });

  return expanded;
};



const generateEventColorMap = (baseMeetings) => {
  const colorMap = {};
  let colorIndex = 0;

  baseMeetings.forEach(event => {
    const key = event.name.trim().toLowerCase(); // normalize name

    if (!colorMap[key]) {
      colorMap[key] = `eventColor${(colorIndex % 15) + 1}`;
      colorIndex++;
    }
  });

  return colorMap;
};





const getColorClass = (event, colorMap) => {
  const key = event.name.trim().toLowerCase();
  return colorMap[key] || 'eventColor1'; // fallback
};



const getWeekDates = (date) => {
  const start = date.startOf('week');
  return Array.from({ length: 7 }, (_, i) => start.add(i, 'day'));
};

const KoCalendar = ({ onAddMeetngs, meetings, className, top=220.1, onEditMeeting, gridHeight,  onDeleteMeeting, isInfoView }) => {
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedEvent] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser] = useState(undefined);
  const [viewMode, setViewMode] = useState('week'); // supports 'month', 'week', 'day'
  const [dayEventModalData, setDayEventModalData] = useState(null);
  const eventColorMap = generateEventColorMap(meetings);
  const allMeetings = useMemo(() => expandRecurringMeetings(meetings), [meetings]);
  const scrollContainerRef = useRef(null);

  const filteredMeetings = useMemo(() => {
    if (!selectedUser) return allMeetings;

    return allMeetings.filter(m =>
      Array.isArray(m.assignees) &&
    m.assignees.some(a => a.userId === selectedUser)
    );
  }, [allMeetings, selectedUser]);



  useEffect(() => {
    console.log('Meetings updated:', meetings);
    console.log('Expanded:', allMeetings);
  }, [meetings, allMeetings]);

  const meetingMap = useMemo(() => {
    const map = {};
    filteredMeetings.forEach(m => {
      const date = dayjs.utc(m.startTime).local().format('YYYY-MM-DD');
      if (!map[date]) map[date] = [];
      map[date].push(m);
    });
    return map;
  }, [filteredMeetings]);


  useEffect(() => {
    console.log('Scroll container height:', scrollContainerRef.current?.scrollHeight);
  }, []);

  useEffect(() => {
    const isToday = selectedDate.isSame(dayjs(), 'day');
    if ((viewMode === 'day' || viewMode === 'week') && isToday) {
      setTimeout(() => {
        scrollToCurrentTime();
      }, 200);
    }
  }, [viewMode, selectedDate]);


  const scrollToCurrentTime = () => {
    if (!scrollContainerRef.current) return;

    const now = dayjs();
    const topOffset = now.hour() * slotHeight + (now.minute() / 60) * slotHeight;

    scrollContainerRef.current.scrollTo({
      top: topOffset - 100,
      behavior: 'smooth'
    });
  };

  const getDurationText = (startTime, endTime) => {
    const start = dayjs.utc(startTime);
    const end = dayjs.utc(endTime);
    const duration = end.diff(start, 'minute');

    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;

    if (hours && minutes) return `${hours}h ${minutes}m`;
    if (hours) return `${hours}h`;
    return `${minutes}m`;
  };


  const getFormattedDuration = (startTime, endTime) => {
    const start = dayjs.utc(startTime).local();
    const end = dayjs.utc(endTime).local();

    const duration = end.diff(start, 'minute');
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;

    return `${start.format('hh:mm')} to ${end.format('hh:mm A')} (${hours}h${minutes ? ` ${minutes}m` : ''})`;
  };

  const content = (eventObj) => {
    return (
      eventObj && <div className={styles.eventContent}>
        <div className={styles.eventNameTitile}>
          {/* <div className={`${styles.eventColor} ${styles[getColorClass(eventObj, eventColorMap)]}`}>
            <KoIcon name='CalendarOutline' width={8} height={8} />
          </div> */}

          <KoTitle ellipsis={
            {
              rows: 2
            }
          } level={3} text={eventObj.name} />
          <div className={styles.eventActionsBtn}>
            {onEditMeeting && getCookie('userId') === eventObj.createdBy && <KoButton className={styles.btn} iconBtn={true} icon={<KoIcon width={14} height={14} name={'EditOutlined'} />} type='text' text="Edit" onClick={() => onEditMeeting(eventObj)} />}
            {onDeleteMeeting  && getCookie('userId') === eventObj.createdBy &&  <KoButton className={styles.btn} iconBtn={true} icon={<KoIcon width={14} height={14} name={'DeleteOutlined'} />} type='text' text="Delete" onClick={() => onDeleteMeeting(eventObj)} />}
          </div>
        </div>
        <KoText className={styles.eventTime} type="secondary" text={getFormattedDuration(eventObj.startTime, eventObj.endTime)} />
        <div className={styles.eventDescriptionWrapper}>
          <KoTitle ellipsis={
            {
              rows: 3
            }
          } className={styles.eventDescription} text={eventObj.description} level={5} />
        </div>
        <div className={styles.eventAssignees}>
          <div className={styles.eventAssignee}>
            {eventObj.assignees?.map((assignee, index) => (
              <div key={index}>
                <KoStaicMembers data={assignee} />
              </div>
            ))}
          </div>
        </div>
        {(eventObj.source === 'event' || eventObj.source === 'meeting') &&   <div className={styles.eventActions}>
          {/* <KoButton text="Decline" onClick={() => { setDayEventModalData(eventObj); setModalOpen(true); }} />
          <KoButton text="Accept" onClick={() => { setDayEventModalData(eventObj); setModalOpen(true); }} />
          <KoButton text="Reschedule" onClick={() => { setDayEventModalData(eventObj); setModalOpen(true); }} />
          <KoButton text="Invite" onClick={() => { setDayEventModalData(eventObj); setModalOpen(true); }} /> */}
          <KoButton href={eventObj.url} target="_blank" className={`${styles[getColorClass(eventObj, eventColorMap)]} ${styles.btn}`} text="Join" type='link' />
        </div>}
      </div>
    );
  };



  const cellRender = (current, info) => {
    if (info.type !== 'date') return info.originNode;

    const dateStr = current.format('YYYY-MM-DD');
    const events = (meetingMap[dateStr] || []).sort((a, b) => getPriority(b) - getPriority(a));

    const maxVisible = 2;
    const visibleEvents = events.slice(0, maxVisible);
    const hiddenEvents = events.slice(maxVisible);

    return (

      <ul className={styles.eventList}>
        {visibleEvents.map(event => (
          <li key={event.meetingId}>
            <Popover
              content={content(event)}
              trigger="click"
              arrow={false}
              title={null}
            >
              <div className={`${styles.dotTag} `}>
                <KoText
                  className={styles.eventName}
                  ellipsis={true}
                  text={`${event.name} (${getDurationText(event.startTime, event.endTime)})`}
                />
              </div>
            </Popover>
          </li>
        ))}
        {hiddenEvents?.length > 0 && (
          <li onClick={() => setDayEventModalData({ date: current, events })}>
            <KoText
              className={styles.eventName}
              ellipsis={true}
              text={`+ ${hiddenEvents.length} more`}
            />
          </li>
        )}
      </ul>


    );
  };

  // const markOverdueMeetings = (data = []) => {
  //   const today = dayjs().startOf('day');
  
  //   return data.map((meeting) => {
  //     const originalEnd = dayjs(meeting.endDate).local().startOf('day');
  
  //     if (meeting.source === 'ticket' && originalEnd.isValid() && originalEnd.isBefore(today)) {
  //       const daysOverdue = today.diff(originalEnd, 'day');
  
  //       return {
  //         ...meeting,
  //         overDue: true,
  //         _originalStartDate: meeting.startDate,
  //         actualCompleteDate: originalEnd.format('YYYY-MM-DD'),
  //         endDate: today.format('YYYY-MM-DD'),
  //         daysOverdue
  //       };
  //     }
  
  //     return {
  //       ...meeting,
  //       _originalStartDate: meeting.startDate
  //     };
  //   });
  // };
  

  const renderDayView = () => {
    const date = selectedDate.startOf('day');
    const hours = Array.from({ length: 24 }, (_, i) =>
      dayjs().hour(i).minute(0).format('hA')
    );
    const dateStr = date.format('YYYY-MM-DD');

    let rawEvents = (meetingMap[dateStr] || []).sort((a, b) => dayjs.utc(a.startTime).diff(dayjs.utc(b.startTime)));
    const grouped = getOverlappingGroups(rawEvents);
    const events = grouped.flatMap(assignColumnsToGroup);

    return (
      <div className={`${styles.dayGridWithTime} ${gridHeight ? gridHeight : ''}`} ref={scrollContainerRef}>
        <div className={styles.timeColumn}>
          {hours.map(hour => (
            <div key={hour} className={styles.timeSlot}>
              {hour}
            </div>
          ))}
        </div>

        <div className={styles.dayColumn}>
          <KoAffix top={top}>
            <div className={`${styles.weekHeaderRow} ${date.isSame(dayjs(), 'day') ? styles.today : ''}`}>
              {date.format('dddd, MMM D')}
            </div>
          </KoAffix>

          <div className={styles.dayContent}>
            {events.map(event => {
              const start = dayjs.utc(event.startTime).local();
              const end = dayjs.utc(event.endTime).local();
              const startHour = start.hour();
              const startMinute = start.minute();
              const durationMinutes = end.diff(start, 'minute');

              const topOffset = (startHour * slotHeight) + (startMinute / 60) * slotHeight;
              const height = (durationMinutes / 60) * slotHeight;
              const width = 100 / event.totalColumns;
              const left = width * event.column;

              let statusClass = styles.ongoing;
              if (dayjs().isAfter(end)) statusClass = styles.completed;
              else if (dayjs().isBefore(start)) statusClass = styles.upcoming;

              return (
                <Popover
                  key={event.meetingId}
                  content={content(event)}
                  trigger="click"
                  arrow={false}
                  title={null}
                >
                  <div
                    className={`${styles.slotCard}`}
                    style={{
                      top: `${topOffset}px`,
                      height: `${height > 35 ? height : height+25}px`,
                      minHeight: `25px`,
                      position: 'absolute',
                      width: `calc(${width}% - 4px)`,
                      left: `calc(${left}% + 2px)`
                    }}
                  >
                    {event?.source !== 'ticket' &&<div className={styles.bookerStatus}>
                      {dayjs().isBetween(start, end) && (
                        <KoButton
                          text="Join"
                          size="small"
                          type="link"
                          className={`${styles.joinButton} `}
                          href={event.url || '#'}
                          target="_blank"
                        />
                      )}
                    </div>}

                    <div className={styles.bookerInfo}>
                      <KoText
                        ellipsis={{ rows: getEllipsisRows(height) }}
                        className={`${styles.eventName} ${statusClass}`}
                        text={event.name}
                      />
                      {event.email && (
                        <KoText ellipsis className={styles.bookerEmail} text={event.email} />
                      )}
                    </div>

                    {event.notes && (
                      <KoDescription className={styles.bookerEmail} ellipsis text={event.notes} />
                    )}
                  </div>
                </Popover>
              );
            })}
          </div>
        </div>
      </div>
    );
  };


  // WEEK VIEW
  const renderWeekView = () => {
    const weekDates = getWeekDates(selectedDate);
    const hours = Array.from({ length: 24 }, (_, i) =>
      dayjs().hour(i).minute(0).format('hA')
    );

    return (
      <div className={`${styles.weekGridWithTime} ${gridHeight ? gridHeight : ''}`} ref={scrollContainerRef}>
        <div className={styles.timeColumn}>
          {hours.map(hour => (
            <div key={hour} className={styles.timeSlot}>
              {hour}
            </div>
          ))}
        </div>

        <div className={styles.weekColumns}>
          <KoAffix top={top}>
            <div className={styles.weekHeaderRow}>
              {weekDates.map(d => (
                <div
                  key={d.format('YYYY-MM-DD')}
                  className={`${styles.weekDay} ${d.isSame(dayjs(), 'day') ? styles.today : ''}`}
                >
                  {d.format('ddd, MMM D')}
                </div>
              ))}
            </div>
          </KoAffix>

          <div className={styles.weekDayContentRow}>
            {weekDates.map(date => {
              const dateStr = date.format('YYYY-MM-DD');
              let rawEvents = (meetingMap[dateStr] || []).sort((a, b) => dayjs.utc(a.startTime).diff(dayjs.utc(b.startTime)));

              const grouped = getOverlappingGroups(rawEvents);
              const events = grouped.flatMap(assignColumnsToGroup);
              
              return (
                <div key={dateStr} className={styles.weekCell}>
                  {events.map(event => {
                    const start = dayjs.utc(event.startTime).local();
                    const end = dayjs.utc(event.endTime).local();
                    const startHour = start.hour();
                    const startMinute = start.minute();
                    const durationMinutes = end.diff(start, 'minute');

                    const topOffset = (startHour * slotHeight) + (startMinute / 60) * slotHeight;
                    const height = (durationMinutes / 60) * slotHeight;
                    const width = 100 / event.totalColumns;
                    const left = width * event.column;

                    let statusClass = styles.ongoing;
                    if (dayjs().isAfter(end)) statusClass = styles.completed;
                    else if (dayjs().isBefore(start)) statusClass = styles.upcoming;

                    return (
                      <Popover
                        key={event.meetingId}
                        content={content(event)}
                        trigger="click"
                        arrow={false}
                        title={null}
                      >
                        <div
                          className={`${styles.slotCard} `}
                          style={{
                            top: `${topOffset}px`,
                            height: `${height > 35 ? height : height+25}px`,
                            minHeight: `25px`,
                            position: 'absolute',
                            width: `calc(${width}% - 4px)`,
                            left: `calc(${left}% + 2px)`
                          }}
                        >
                          {event?.source !== 'ticket' &&<div className={styles.bookerStatus}>
                            {dayjs().isBetween(start, end) && (
                              <KoButton
                                text="Join"
                                size="small"
                                type="link"
                                className={`${styles.joinButton} `}
                                href={event.url || '#'}
                                target="_blank"
                              />
                            )}
                          </div>}

                          <div className={styles.bookerInfo}>
                            <div className={styles.bookNameTime}>
                              <div className={styles.bookerInfoText}>
                                {event.source === 'ticket' && <Link target="_blank" to={`/open/ticket/${getCookie("accountId")}/${event.workspaceId}/${event.meetingId}`}>
                                  <div className={styles.ticektWrapper}>
                                    <div className={styles.ticketIcon} style={{borderColor:  event.statusDetails.color}}/>
                                    <KoText text= {event.meetingId} className={styles.ticketId}/>
                                  </div>
                                </Link>}
                                <KoText
                                  ellipsis={{ rows: getEllipsisRows(height) }}
                                  className={`${styles.eventName} ${statusClass}`}
                                  text={event.name}
                                />
                              </div>
                              <div className={styles.overdue}>
                                <KoText className={styles.eventTime} text={`${start.format('h:mm')} - ${end.format('h:mm a')}`} />
                                {event.overDue > 0 &&
                                  <KoTooltips title={`Overdue ${event.overDue ? event.overDue : 0}d`}>
                                    <KoText ellipsis={true} className={styles.span} text={`${event.overDue ? event.overDue : 0}d`} />
                                  </KoTooltips>
                                }
                              </div>
                            </div>
                            {event.email && (
                              <KoText ellipsis className={styles.bookerEmail} text={event.email} />
                            )}
                          </div>
                          {event.notes && (
                            <KoDescription className={styles.bookerEmail} ellipsis text={event.notes} />
                          )}
                        </div>
                      </Popover>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };


  const viewModes = [
    { key: 'month', label: 'Month' },
    { key: 'week', label: 'Week' },
    { key: 'day', label: 'Day' }
  ];

  let { monthShort, day, fullMonthYear, rangeText, weekRangeText } = getCurrentMonthDisplayInfo(selectedDate);
  return (
    <div className={`${styles.calendarWrapper} ${!isInfoView ? styles.infoViewWrapper : ''}`}>
      {isInfoView && <div className={styles.sideBar}>
        <div className={styles.koEngagementCalendar}>
          <KoEngagementCalendar meetings={_deepClone(meetings)} value={selectedDate} onSelect={setSelectedDate} />
        </div>
        <div className={styles.chart}>
          <KoTitle text={`Current Week Booking Trend`} level={5} />
          <KoCharts option={BookingTrendChart(meetings)} />
        </div>
      </div>}
      <div className={`${styles.mainCalendar} ${!isInfoView ? styles.infoViewMain : ''} fullCalendar ${className ? className : ''}`}>
        <div className={styles.calendarHeader}>
          <div className={styles.calendarTitle}>
            <div className={styles.monthTitle}>
              <KoText className={styles.monthLabel} text={monthShort} />
              <KoText className={styles.dateLabel} text={day} />
            </div>
            <div className={styles.yearTitle}>
              <KoText className={styles.yearLabel} text={fullMonthYear} />
              <KoText className={styles.dateLabel} text={viewMode === 'week' ? weekRangeText : rangeText} />
            </div>
          </div>
          <div className={styles.calendarActions}>
            <KoDropdown
              menu={{
                items: viewModes,
                onClick: ({ key }) => setViewMode(key)
              }}
              placement="bottomRight"
            >
              <div className={styles.viewMode}>
                <KoText text={viewModes.find(item => item.key === viewMode).label} />
                <KoIcon name="DownArrowOutlined" color="#004b50" width={10} height={10} />
              </div>
            </KoDropdown>
            {onAddMeetngs && <KoButton type="primary" icon={<KoIcon color="#fff" name="PlusOutlined" />} text={'Add Meeting'} onClick={() => onAddMeetngs()} />}
          </div>
        </div>
        {viewMode === 'month' && (
          <>
            <div className={styles.customWeekHeader}>
              {['Mon', 'Tues', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                <div key={day} className={styles.weekday}>
                  {day}
                </div>
              ))}
            </div>
            <Calendar
              value={selectedDate}
              onSelect={setSelectedDate}
              cellRender={cellRender}
              fullscreen
              headerRender={() => null}
            />
          </>
        )}

        {viewMode === 'week' && renderWeekView()}
        {viewMode === 'day' && renderDayView()}


        <Modal
          title={selectedEvent?.name}
          open={modalOpen}
          onCancel={() => setModalOpen(false)}
          footer={null}
        >
          <p><b>Time:</b> {dayjs.utc(selectedEvent?.startTime).local().format('ddd, MMM D, YYYY hh:mm A')}</p>
          <p><b>Link:</b> <a href={selectedEvent?.url} target="_blank" rel="noopener noreferrer">{selectedEvent?.url}</a></p>
          <p><b>Description:</b> {selectedEvent?.description}</p>
        </Modal>

        <Modal
          title={`Events on ${dayEventModalData?.date.format('ddd, MMM D')}`}
          open={!!dayEventModalData}
          onCancel={() => setDayEventModalData(null)}
          footer={null}
        >
          {dayEventModalData?.events?.map(event => (
            <div key={event.meetingId} style={{ marginBottom: 12 }}>
              <Tag color={userColorMap[event.source] || 'default'}>{event.name}</Tag>
              <div style={{ fontSize: 12 }}>
                <b>Time:</b> {dayjs.utc(event.startTime).local().format('hh:mm A')}<br />
                <b>Description:</b> {event.description}
              </div>
            </div>
          ))}
        </Modal>
      </div>
    </div>
  );
};

export default KoCalendar;
